import * as THREE from 'three';
import { gridOf, rowsOf, tileCount, type Board, type Cell, type Group, type TileMove } from '../puzzle';
import { boardRect, cellCenter, cellRect, type BoardRect, type Viewport } from './layout';
import { insetsFrom, quadFor, sideClosure, tileUV, type Sides } from '../fx/tileQuad';
import { outlineMesh } from '../fx/outline';
import { Tweens } from '../fx/tween';
import { createPaperTexture, createShadowTexture } from '../fx/paperTexture';
import { createRotateBadgeTexture, createTileBackTexture } from '../fx/modTextures';

const GAP = 2;
const CLOSE_PER_MS = 1 / 180;
const OPEN_PER_MS = 1 / 120;
const WARM = new THREE.Color(1.35, 1.22, 0.95);
const WHITE = new THREE.Color(1, 1, 1);
const SIDES = ['l', 'r', 't', 'b'] as const;
const FULL_UV = { u0: 0, v0: 0, u1: 1, v1: 1 };
/** 点一下转 90° 的动画时长；背面翻开的时长 */
export const ROTATE_MS = 150;
export const FLIP_MS = 300;

type TileView = {
  mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  closure: Sides<number>;
  target: Sides<boolean>;
  offset: THREE.Vector2;
  lift: number;
  scale: number;
  pivot: THREE.Vector2;
  dirty: boolean;
  /** 当前拥有该块偏移的补间令牌；0 = 手指/无人 */
  owner: number;
  /** 显示角度（弧度，顺时针为负）；rot = k 时静止在 −k·π/2 */
  angle: number;
  /** 翻面动画的横向缩放（1 = 平放；翻到一半时为 0） */
  flipX: number;
  /** 当前画的是纸背 */
  back: boolean;
  /** 旋转角标（右上角，不随块转动） */
  badge: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  badgeOn: boolean;
};

export type Mark = { cells: Cell[]; color: number; pulse?: boolean };

export class BoardScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 100);
  private rect: BoardRect = { cx: 0, cy: 0, w: 1, h: 1 };
  /** 图的宽 / 高（城市 1；塔罗竖版 2/3）：棋盘按图的比例摆，立体书交接时逐像素对齐 */
  readonly aspect: number;
  private board: Board | null = null;
  private tiles: TileView[] = [];
  private readonly tweens = new Tweens();
  private readonly paper: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly shadow: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly backing: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly preview: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly marks = new THREE.Group();
  private markSpec: Mark[] = [];
  private markTime = 0;
  private tweenToken = 0;
  private readonly backTex = createTileBackTexture();
  private readonly badgeTex = createRotateBadgeTexture();

  constructor(private readonly picture: THREE.Texture) {
    this.aspect = pictureAspect(picture);
    this.paper = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: createPaperTexture() }));
    this.paper.position.z = -10;
    this.shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: createShadowTexture(), transparent: true, depthWrite: false }),
    );
    this.shadow.position.z = -9;
    this.backing = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ color: 0xe2d6bd }));
    this.backing.position.z = -8;
    this.preview = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: picture, transparent: true, opacity: 0, depthWrite: false }),
    );
    this.preview.position.z = 30;
    this.preview.visible = false;
    this.marks.position.z = 20;
    this.scene.add(this.paper, this.shadow, this.backing, this.marks, this.preview);
  }

  get boardRect(): BoardRect {
    return this.rect;
  }

  setViewport(vp: Viewport): void {
    this.camera.left = -vp.width / 2;
    this.camera.right = vp.width / 2;
    this.camera.top = vp.height / 2;
    this.camera.bottom = -vp.height / 2;
    this.camera.updateProjectionMatrix();
    this.rect = boardRect(vp, this.aspect);
    this.paper.scale.set(vp.width, vp.height, 1);
    this.paper.material.map!.repeat.set(vp.width / 256, vp.height / 256);
    const { w, h } = this.rect;
    this.backing.position.set(this.rect.cx, this.rect.cy, -8);
    // 只垫在棋盘内（瓦片缝隙里透出），外沿交给 DOM 纸板框（#board-frame）
    this.backing.scale.set(w, h, 1);
    this.shadow.position.set(this.rect.cx + 4, this.rect.cy - 8, -9);
    this.shadow.scale.set(w * 1.5, h * 1.5, 1);
    this.preview.position.set(this.rect.cx, this.rect.cy, 30);
    this.preview.scale.set(w, h, 1);
    this.tiles.forEach((v) => (v.dirty = true));
    if (this.board) this.setMarks(this.markSpec);
  }

  /** instant=true 时缝隙直接跳到目标状态（开局/重置） */
  setBoard(board: Board, instant = false): void {
    // 先收尾旧补间（它们引用旧 tile id），再按需重建瓦片。
    // clear() 在某个回调出错时会在全部收尾后重新抛错；用 finally 保证重建照常进行，错误仍向上暴露
    try {
      if (instant) this.tweens.clear();
    } finally {
      this.applyBoard(board, instant);
    }
  }

  private applyBoard(board: Board, instant: boolean): void {
    const rebuilt = !this.board || this.board.n !== board.n || rowsOf(this.board) !== rowsOf(board);
    if (rebuilt) this.buildTiles(tileCount(board));
    const prev = rebuilt ? null : this.board;
    this.board = board;
    const sides = sideClosure(board);
    this.tiles.forEach((v, i) => {
      v.target = sides[i];
      const rot = board.rot?.[i] ?? 0;
      const down = !!board.down?.[i];
      if (instant || !prev) {
        v.angle = down ? 0 : -rot * (Math.PI / 2);
        v.flipX = 1;
        v.back = down;
        v.badgeOn = rot !== 0 && !down;
      } else {
        const was = prev.rot?.[i] ?? 0;
        const wasDown = !!prev.down?.[i];
        if (wasDown && !down) this.flipUp(i);
        else if (!down && rot !== was) this.spin(i, (rot - was + 4) % 4);
        else if (down && !wasDown) {
          v.back = true;
          v.badgeOn = false;
        }
      }
      if (instant) {
        v.closure = { l: +sides[i].l, r: +sides[i].r, t: +sides[i].t, b: +sides[i].b };
        v.offset.set(0, 0);
        v.scale = 1;
        v.lift = 0;
        v.mesh.material.color.copy(WHITE);
      }
      v.dirty = true;
    });
  }

  /** 旋转块顺时针转 quarters 个 90°（补间结束时对齐到 −rot·π/2，角标在转正时收起） */
  private spin(i: number, quarters: number): void {
    const v = this.tiles[i];
    const from = v.angle;
    const to = from - quarters * (Math.PI / 2);
    void this.tweens.add(ROTATE_MS, (k) => {
      v.angle = from + (to - from) * k;
      if (k >= 1) {
        const rot = this.board?.rot?.[i] ?? 0;
        v.angle = -rot * (Math.PI / 2);
        v.badgeOn = rot !== 0 && !this.board?.down?.[i];
      }
      v.dirty = true;
    });
  }

  /** 背面块翻开：横向压扁到 0 → 换成画面 → 展开，翻开那一下提亮 */
  private flipUp(i: number): void {
    const v = this.tiles[i];
    const rot = this.board?.rot?.[i] ?? 0;
    void this.tweens.add(FLIP_MS, (k) => {
      v.flipX = Math.max(0.02, Math.abs(Math.cos(Math.PI * k)));
      if (k >= 0.5 && v.back) {
        v.back = false;
        v.angle = -rot * (Math.PI / 2);
        v.mesh.material.map = this.picture;
        v.mesh.material.color.copy(WARM);
      }
      if (k >= 0.5) v.mesh.material.color.copy(WARM).lerp(WHITE, (k - 0.5) * 2);
      if (k >= 1) {
        v.flipX = 1;
        v.badgeOn = rot !== 0 && !!this.board?.rot?.[i];
      }
      v.dirty = true;
    });
  }

  /** 拖动中：整单位跟手，抬高渲染层级 */
  setOffset(tiles: number[], dx: number, dy: number, lift: number): void {
    for (const id of tiles) {
      const v = this.tiles[id];
      v.offset.set(dx, dy);
      v.lift = lift;
      v.owner = 0;
      v.dirty = true;
    }
  }

  /** 把这些块当前的视觉偏移补间回 0（弹回 / 落位） */
  settle(tiles: number[], ms: number): Promise<void> {
    const starts = tiles.map((id) => this.tiles[id].offset.clone());
    const token = ++this.tweenToken;
    for (const id of tiles) this.tiles[id].owner = token;
    return this.tweens.add(ms, (k) => {
      tiles.forEach((id, i) => {
        const v = this.tiles[id];
        if (v.owner !== token) return; // 已被更新的补间或手指接管
        v.offset.copy(starts[i]).multiplyScalar(1 - k);
        if (k >= 1) v.lift = 0;
        v.dirty = true;
      });
    });
  }

  /** 棋盘已更新后调用：让每个移动过的块从旧的视觉位置滑到新格 */
  slideFrom(moves: TileMove[], ms = 140): Promise<void> {
    if (!this.board) return Promise.resolve();
    const g = gridOf(this.board);
    for (const m of moves) {
      const a = cellCenter(this.rect, g, m.from);
      const b = cellCenter(this.rect, g, m.to);
      const v = this.tiles[m.tile];
      v.offset.set(a.x - b.x + v.offset.x, a.y - b.y + v.offset.y);
      if (v.lift === 0) v.lift = 2;
    }
    return this.settle(moves.map((m) => m.tile), ms);
  }

  /** 新粘连：整组 1.06 回弹 + 暖色提亮 + 外轮廓金光淡出 */
  mergePop(groups: Group[]): Promise<void> {
    if (!this.board || groups.length === 0) return Promise.resolve();
    const { pos } = this.board;
    const n = gridOf(this.board);
    // 金色轮廓跟随整组一起回弹（同一 pivot、同一缩放），否则放大时轮廓会落在图块内部
    const flashes: { mesh: THREE.Object3D | null; pivot: THREE.Vector2 }[] = [];
    for (const g of groups) {
      let x = 0;
      let y = 0;
      for (const id of g) {
        const c = cellCenter(this.rect, n, pos[id]);
        x += c.x;
        y += c.y;
      }
      const pivot = new THREE.Vector2(x / g.length, y / g.length);
      for (const id of g) this.tiles[id].pivot.copy(pivot);
      flashes.push({ mesh: this.flashOutline(g.map((id) => pos[id]), 0xf6c25b, 520), pivot });
    }
    const ids = groups.flat();
    void this.tweens.add(520, (k) => {
      for (const id of ids) this.tiles[id].mesh.material.color.copy(WARM).lerp(WHITE, k);
    });
    return this.tweens.add(
      300,
      (k) => {
        const s = 1 + 0.06 * Math.sin(Math.PI * k);
        for (const id of ids) {
          const v = this.tiles[id];
          v.scale = s;
          v.dirty = true;
        }
        for (const f of flashes) {
          if (!f.mesh) continue;
          f.mesh.scale.set(s, s, 1);
          f.mesh.position.set(f.pivot.x * (1 - s), f.pivot.y * (1 - s), f.mesh.position.z);
        }
      },
      (t) => t,
    );
  }

  showPreview(ms = 3000): Promise<void> {
    const m = this.preview.material;
    this.preview.visible = true;
    return this.tweens.add(
      ms,
      (k) => {
        m.opacity = 0.85 * (k < 0.08 ? k / 0.08 : k > 0.85 ? (1 - k) / 0.15 : 1);
        if (k >= 1) this.preview.visible = false;
      },
      (t) => t,
    );
  }

  setMarks(marks: Mark[]): void {
    this.markSpec = marks;
    for (const child of [...this.marks.children]) {
      this.marks.remove(child);
      const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
    if (!this.board) return;
    for (const mk of marks) {
      const mesh = outlineMesh(this.rect, gridOf(this.board), mk.cells, 3, mk.color);
      mesh.userData.pulse = !!mk.pulse;
      this.marks.add(mesh);
    }
  }

  update(dtMs: number): void {
    this.tweens.update(dtMs);
    this.markTime += dtMs;
    for (const child of this.marks.children) {
      const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
      mesh.material.opacity = mesh.userData.pulse ? 0.55 + 0.45 * Math.sin(this.markTime / 180) : 1;
    }
    for (let i = 0; i < this.tiles.length; i++) {
      const v = this.tiles[i];
      for (const side of SIDES) {
        const goal = v.target[side] ? 1 : 0;
        const cur = v.closure[side];
        if (cur === goal) continue;
        v.closure[side] = goal > cur ? Math.min(goal, cur + CLOSE_PER_MS * dtMs) : Math.max(goal, cur - OPEN_PER_MS * dtMs);
        v.dirty = true;
      }
      if (v.dirty) this.applyTile(i);
    }
  }

  private flashOutline(cells: Cell[], color: number, ms: number): THREE.Object3D | null {
    if (!this.board) return null;
    const mesh = outlineMesh(this.rect, gridOf(this.board), cells, 4, color);
    mesh.position.z = 25;
    this.scene.add(mesh);
    void this.tweens.add(ms, (k) => {
      mesh.material.opacity = 1 - k;
      if (k >= 1) {
        this.scene.remove(mesh);
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
    });
    return mesh;
  }

  private buildTiles(count: number): void {
    for (const v of this.tiles) {
      this.scene.remove(v.mesh, v.badge);
      v.mesh.geometry.dispose();
      v.mesh.material.dispose();
      v.badge.geometry.dispose();
      v.badge.material.dispose();
    }
    this.tiles = Array.from({ length: count }, () => {
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: this.picture }));
      mesh.frustumCulled = false; // 顶点每帧改写，初始包围球不可信
      const badge = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({ map: this.badgeTex, transparent: true, depthWrite: false }),
      );
      badge.frustumCulled = false;
      badge.visible = false;
      this.scene.add(mesh, badge);
      return {
        mesh,
        closure: { l: 0, r: 0, t: 0, b: 0 },
        target: { l: false, r: false, t: false, b: false },
        offset: new THREE.Vector2(),
        lift: 0,
        scale: 1,
        pivot: new THREE.Vector2(),
        dirty: true,
        owner: 0,
        angle: 0,
        flipX: 1,
        back: false,
        badge,
        badgeOn: false,
      };
    });
  }

  /** 释放 GPU 资源（离开拼图页时）；原图纹理归调用方（与立体书共用） */
  dispose(): void {
    this.tweens.clear();
    this.setMarks([]);
    this.paper.material.map?.dispose();
    this.shadow.material.map?.dispose();
    this.backTex.dispose();
    this.badgeTex.dispose();
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh<THREE.BufferGeometry, THREE.Material>;
      if (!m.isMesh) return;
      m.geometry.dispose();
      m.material.dispose();
    });
    this.scene.clear();
    this.tiles = [];
    this.board = null;
  }

  private applyTile(i: number): void {
    if (!this.board) return;
    const v = this.tiles[i];
    const n = this.board.n;
    const cell = cellRect(this.rect, gridOf(this.board), this.board.pos[i]);
    const q = quadFor(cell, v.back ? FULL_UV : tileUV(i, n, rowsOf(this.board)), insetsFrom(v.closure, GAP));
    const map = v.back ? this.backTex : this.picture;
    if (v.mesh.material.map !== map) v.mesh.material.map = map;
    const geo = v.mesh.geometry;
    (geo.attributes.position.array as Float32Array).set(q.positions);
    geo.attributes.position.needsUpdate = true;
    (geo.attributes.uv.array as Float32Array).set(q.uvs);
    geo.attributes.uv.needsUpdate = true;
    const cx = (cell.x0 + cell.x1) / 2;
    const cy = (cell.y0 + cell.y1) / 2;
    const px = v.pivot.x + (cx - v.pivot.x) * v.scale;
    const py = v.pivot.y + (cy - v.pivot.y) * v.scale;
    v.mesh.position.set(px + v.offset.x, py + v.offset.y, v.lift);
    // 长方形格子（竖版 5×7 / 7×10 的格子宽高差 5–7%）转到横着时按短边缩一点，不压到邻格
    const cw = cell.x1 - cell.x0;
    const ch = cell.y1 - cell.y0;
    const fit = 1 - (1 - Math.min(cw, ch) / Math.max(cw, ch)) * Math.abs(Math.sin(v.angle));
    v.mesh.scale.set(v.scale * v.flipX * fit, v.scale * fit, 1);
    v.mesh.rotation.z = v.angle;
    // 角标：贴在格子右上角内侧，不随块转；9×9（格子约 37px）时约 13px，大格最多 22px
    const s = Math.min(cw, ch);
    const b = Math.min(22, Math.max(12, s * 0.34));
    v.badge.visible = v.badgeOn;
    if (v.badgeOn) {
      const inset = b / 2 + Math.max(2, s * 0.05);
      v.badge.position.set(px + v.offset.x + (cw / 2 - inset) * v.scale, py + v.offset.y + (ch / 2 - inset) * v.scale, v.lift + 0.5);
      v.badge.scale.set(b * v.scale, b * v.scale, 1);
    }
    v.dirty = false;
  }
}

/** 纹理图片的宽 / 高；接近 1（±1%）的当作正方形，城市图走与以前完全相同的布局 */
export function pictureAspect(tex: THREE.Texture): number {
  const img = tex.image as { width?: number; height?: number } | null;
  const a = img?.width && img?.height ? img.width / img.height : 1;
  return Math.abs(a - 1) < 0.01 ? 1 : a;
}
